package top.wcpe.allincore.ui.web.preview

import top.wcpe.allincore.dsl.compiler.api.IdeMetadata
import top.wcpe.allincore.dsl.compiler.pipeline.CompileResult
import top.wcpe.allincore.dsl.compiler.pipeline.ScreenWithComponentsPipeline
import top.wcpe.allincore.dsl.compiler.source.SourceFile
import top.wcpe.allincore.dsl.ir.codec.IrDecoder
import top.wcpe.allincore.dsl.ir.model.UiBinding
import top.wcpe.allincore.dsl.ir.model.UiIr
import top.wcpe.allincore.dsl.ir.model.UiNode
import top.wcpe.allincore.ui.web.asset.AssetPathPolicy
import top.wcpe.allincore.ui.web.asset.AssetReferenceKind
import top.wcpe.allincore.ui.web.asset.AuthoritativeReadSet
import top.wcpe.allincore.ui.web.asset.PreviewAssetResolver
import top.wcpe.allincore.ui.web.asset.PreviewBudgetRetirement
import top.wcpe.allincore.ui.web.asset.PreviewDependencyScanner
import top.wcpe.allincore.ui.web.asset.ObjectUrlLease
import top.wcpe.allincore.ui.web.asset.PreviewReadAsset
import top.wcpe.allincore.ui.web.asset.PreviewReadKind
import top.wcpe.allincore.ui.web.asset.ResolvedAsset
import top.wcpe.allincore.ui.web.mock.MockRoutes
import top.wcpe.allincore.ui.web.mock.parseMockScenario
import top.wcpe.allincore.ui.web.model.ThreePreparedModelSet
import top.wcpe.allincore.ui.web.model.ThreeStaticModelPreparer
import top.wcpe.allincore.ui.web.model.ThreeStaticModelRequest
import kotlin.js.Promise

internal enum class PreviewModelAssetProperty {
    SOURCE,
    TEXTURE,
}

internal data class PreviewModelAssetRequest(
    val nodeId: String,
    val property: PreviewModelAssetProperty,
    val baseCanonicalDirectory: String,
    val kind: AssetReferenceKind,
    val binding: UiBinding,
    val resolvedCanonicalPath: String?,
) {
    val requiresRuntimeResolution: Boolean
        get() = resolvedCanonicalPath == null
}

internal class PreviewCandidateBackground(val objectUrl: ObjectUrlLease) {
    private var load: PreviewBackgroundLoad? = null
    private var disposeRequested = false
    private var rawReleased = false

    internal fun attach(owner: PreviewBackgroundLoad) {
        check(load == null) { "背景加载所有权不可重复绑定" }
        load = owner
        if (disposeRequested) owner.dispose()
    }

    fun dispose() {
        disposeRequested = true
        val owner = load
        if (owner != null) {
            owner.dispose()
            return
        }
        if (!rawReleased && objectUrl.tryDispose()) rawReleased = true
    }
}

internal data class PreviewCandidate(
    val canonicalPath: String,
    val ir: UiIr,
    val sourceReadSet: AuthoritativeReadSet?,
    val assets: List<PreviewReadAsset>,
    val modelAssetRequests: List<PreviewModelAssetRequest>,
    val mockRoutes: MockRoutes = emptyMap(),
    val background: PreviewCandidateBackground? = null,
    val preparedModels: ThreePreparedModelSet? = null,
)

internal class PreviewCandidateOwnership(
    private val commitAction: () -> Unit,
    private val discardAction: () -> Unit,
    internal val budgetRetirement: PreviewBudgetRetirement? = null,
    internal val retirements: List<() -> Unit> = emptyList(),
    private val confirmAction: () -> Unit = {},
) {
    private var state = State.OPEN

    fun commit() {
        if (state != State.OPEN) return
        state = State.COMMITTING
        try {
            commitAction()
            state = State.COMMITTED
        } catch (error: Throwable) {
            state = State.OPEN
            throw error
        }
    }

    fun confirm() {
        if (state != State.COMMITTED) return
        confirmAction()
        state = State.CONFIRMED
    }

    fun discard() {
        if (state != State.OPEN) return
        state = State.DISCARDED
        discardAction()
    }

    private enum class State {
        OPEN,
        COMMITTING,
        COMMITTED,
        CONFIRMED,
        DISCARDED,
    }
}

internal sealed interface PreviewCandidateLoadResult {
    data class Success(
        val candidate: PreviewCandidate,
        val ownership: PreviewCandidateOwnership,
    ) : PreviewCandidateLoadResult

    data class Failure(val diagnostics: List<String>) : PreviewCandidateLoadResult
}

internal fun interface PreviewCandidateSource {
    fun load(path: String): Promise<PreviewCandidateLoadResult>

    fun probePublishedChanges(): Promise<Boolean> = Promise.resolve(false)

    fun invalidatePublishedReadSet() = Unit
}

internal class PreviewCandidateLoader(
    private val resolver: PreviewAssetResolver,
    private val scanner: PreviewDependencyScanner = PreviewDependencyScanner(resolver),
    private val backgroundReference: String? = null,
    modelPreparer: ThreeStaticModelPreparer? = null,
) : PreviewCandidateSource {
    // scanner 可由调用方注入（docs 模式登记组件 alias）；默认空 registry 仅支持相对 import
    private val pendingChangedPaths = linkedSetOf<String>()
    private var modelPreparer = modelPreparer
    private var invalidateAll = false

    override fun load(path: String): Promise<PreviewCandidateLoadResult> {
        resolver.reload(consumeInvalidation(path))
        val generation = resolver.generation
        return resolver.resolveBytes(path).flatMap { asset ->
            loadResolved(asset).then { candidate -> finishSuccess(generation, candidate) }
        }.catch { throwable -> finishFailure(generation, throwable) }
    }

    internal fun bindModelPreparer(preparer: ThreeStaticModelPreparer?) {
        if (preparer != null) modelPreparer = preparer
    }

    override fun probePublishedChanges(): Promise<Boolean> {
        val generation = resolver.generation
        return resolver.probePublishedReadSetVersions().then { changed ->
            if (resolver.generation != generation || changed.isEmpty()) return@then false
            if (!invalidateAll) pendingChangedPaths += changed
            true
        }
    }

    override fun invalidatePublishedReadSet() {
        invalidateAll = true
        pendingChangedPaths.clear()
    }

    private fun consumeInvalidation(rootPath: String): Set<String>? {
        if (invalidateAll) {
            invalidateAll = false
            return null
        }
        if (pendingChangedPaths.isEmpty()) return setOf(rootPath)
        return pendingChangedPaths.toSet().also { pendingChangedPaths.clear() }
    }

    private fun finishSuccess(generation: Long, candidate: PreviewCandidate): PreviewCandidateLoadResult {
        if (resolver.generation != generation) {
            candidate.disposePreparedResources()
            return PreviewCandidateLoadResult.Failure(listOf(STALE_DIAGNOSTIC))
        }
        val prepared = candidate.copy(assets = resolver.candidateReadSetSnapshot())
        return PreviewCandidateLoadResult.Success(prepared, ownershipFor(generation, prepared))
    }

    private fun ownershipFor(
        generation: Long,
        candidate: PreviewCandidate,
    ): PreviewCandidateOwnership {
        val retirement = resolver.candidateBudgetRetirement(generation)
        return PreviewCandidateOwnership(
            commitAction = {
                if (resolver.generation == generation) resolver.publishCandidate()
            },
            discardAction = {
                candidate.disposePreparedResources()
                if (resolver.generation == generation) resolver.discardCandidate()
            },
            budgetRetirement = retirement,
        )
    }

    private fun finishFailure(generation: Long, throwable: Throwable): PreviewCandidateLoadResult {
        if (resolver.generation == generation) {
            resolver.discardCandidate()
        }
        return PreviewCandidateLoadResult.Failure(listOf(chineseFailure(throwable)))
    }

    private fun loadResolved(asset: ResolvedAsset<ByteArray>): Promise<PreviewCandidate> {
        val candidate = if (asset.mime == AICR_MIME) decodeAicr(asset) else null
        return if (candidate != null) {
            prepareCandidate(asset, candidate)
        } else {
            loadSource(asset).flatMap { prepareCandidate(asset, it) }
        }
    }

    private fun loadSource(asset: ResolvedAsset<ByteArray>): Promise<PreviewCandidate> =
        scanner.scanCandidate(asset.canonicalPath).then { readSet ->
            val root = SourceFile(readSet.root.canonicalPath, readSet.root.content)
            // 组件源与主题源分列，保证 themeDirectory / availableThemes 进入 IR
            val components = readSet.dependencies
                .filter { it.kind == PreviewReadKind.SOURCE_IMPORT }
                .map { SourceFile(it.canonicalPath, it.content) }
            val themes = readSet.dependencies
                .filter { it.kind == PreviewReadKind.THEME }
                .map { SourceFile(it.canonicalPath, it.content) }
            when (val result = ScreenWithComponentsPipeline().compile(components, themes, root)) {
                is CompileResult.Success -> emptyCandidate(asset.canonicalPath, result.ir, readSet)
                is CompileResult.Failure -> throw IllegalArgumentException(compileDiagnostic(result))
            }
        }

    private fun decodeAicr(asset: ResolvedAsset<ByteArray>): PreviewCandidate =
        try {
            emptyCandidate(asset.canonicalPath, IrDecoder.decodeFromBytes(asset.content), null)
        } catch (throwable: Throwable) {
            throw IllegalArgumentException("AICR 解码失败：${throwable.message ?: "数据无效"}", throwable)
        }

    private fun prepareCandidate(
        rootAsset: ResolvedAsset<ByteArray>,
        candidate: PreviewCandidate,
    ): Promise<PreviewCandidate> {
        val seeds = mutableListOf<ModelAssetSeed>()
        collectModelAssetSeeds(candidate.ir.root, seeds)
        return loadMockRoutes(rootAsset).flatMap { routes ->
            resolveModelAssets(rootAsset, seeds, 0, mutableListOf()).flatMap { requests ->
                prepareCandidateResources(rootAsset, candidate, routes, requests)
            }
        }
    }

    private fun prepareCandidateResources(
        rootAsset: ResolvedAsset<ByteArray>,
        candidate: PreviewCandidate,
        routes: MockRoutes,
        requests: List<PreviewModelAssetRequest>,
    ): Promise<PreviewCandidate> = loadBackground(rootAsset).flatMap { background ->
        prepareStaticModels(requests).then { models ->
            candidate.copy(modelAssetRequests = requests, mockRoutes = routes, background = background, preparedModels = models)
        }.catch { reason ->
            background?.dispose()
            throw asThrowable(reason)
        }
    }

    private fun prepareStaticModels(requests: List<PreviewModelAssetRequest>): Promise<ThreePreparedModelSet?> {
        val staticRequests = staticModelRequests(requests)
        val preparer = modelPreparer ?: return if (staticRequests.isEmpty()) {
            Promise.resolve(null)
        } else {
            Promise.reject(IllegalStateException("静态模型预备器尚未绑定"))
        }
        return preparer.prepareStaticModels(staticRequests).then<ThreePreparedModelSet?> { it }
    }

    private fun loadMockRoutes(rootAsset: ResolvedAsset<ByteArray>): Promise<MockRoutes> {
        val mockPath = rootAsset.canonicalPath.substringBeforeLast('.') + MOCK_SUFFIX
        return resolver.resolveOptionalRegistered(rootAsset.fileProvenance, mockPath, AssetReferenceKind.MOCK).then { mock ->
            if (mock == null) emptyMap() else parseMockScenario(mock.content.decodeToString())
        }
    }

    private fun loadBackground(rootAsset: ResolvedAsset<ByteArray>): Promise<PreviewCandidateBackground?> {
        val reference = backgroundReference ?: return Promise.resolve(null)
        return resolver.resolveRelative(rootAsset.fileProvenance, reference, AssetReferenceKind.BACKGROUND).then { asset ->
            PreviewCandidateBackground(resolver.acquireObjectUrl(asset))
        }
    }

    private fun collectModelAssetSeeds(node: UiNode, output: MutableList<ModelAssetSeed>) {
        if (node.kind == MODEL_PREVIEW_KIND) {
            appendModelAssetSeeds(node, output)
        }
        node.children.forEach { collectModelAssetSeeds(it, output) }
    }

    private fun appendModelAssetSeeds(node: UiNode, output: MutableList<ModelAssetSeed>) {
        val base = validatedModelBase(node)
        val source = node.props[SOURCE_PROP]
            ?: throw IllegalArgumentException("modelPreview '${node.id}' 缺少 source 绑定")
        appendSeed(node.id, SOURCE_PROP, base, AssetReferenceKind.MODEL, source, output)
        node.props[TEXTURE_PROP]?.let {
            appendSeed(node.id, TEXTURE_PROP, base, AssetReferenceKind.TEXTURE, it, output)
        }
    }

    private fun validatedModelBase(node: UiNode): String {
        val binding = node.props[IdeMetadata.modelPreviewBaseProp]
            ?: throw IllegalArgumentException("modelPreview '${node.id}' 缺少内部 base")
        if (binding.type != UiBinding.BindingType.LITERAL || binding.literalValue !is String) {
            throw IllegalArgumentException("modelPreview '${node.id}' 内部 base 类型错误")
        }
        return try {
            val base = AssetPathPolicy.canonicalDirectory(binding.literalValue as String)
            if (base.isNotEmpty() && AssetPathPolicy.external(base) != base) {
                throw IllegalArgumentException("内部基准目录不是规范路径")
            }
            base
        } catch (throwable: Throwable) {
            throw IllegalArgumentException("modelPreview '${node.id}' 内部 base 非法：${throwable.message}", throwable)
        }
    }

    private fun appendSeed(
        nodeId: String,
        property: String,
        base: String,
        kind: AssetReferenceKind,
        binding: UiBinding,
        output: MutableList<ModelAssetSeed>,
    ) {
        if (binding.type == UiBinding.BindingType.LITERAL && binding.literalValue == null && property == TEXTURE_PROP) {
            return
        }
        validateModelBinding(nodeId, property, binding)
        output += ModelAssetSeed(nodeId, property, base, kind, binding)
    }

    private fun validateModelBinding(nodeId: String, property: String, binding: UiBinding) {
        when (binding.type) {
            UiBinding.BindingType.LITERAL -> if (binding.literalValue !is String) {
                throw IllegalArgumentException("modelPreview '$nodeId' 的 $property 静态值类型错误")
            }
            UiBinding.BindingType.EXPR -> Unit
            UiBinding.BindingType.EVENT -> {
                throw IllegalArgumentException("modelPreview '$nodeId' 的 $property 不允许事件绑定")
            }
        }
    }

    private fun resolveModelAssets(
        rootAsset: ResolvedAsset<ByteArray>,
        seeds: List<ModelAssetSeed>,
        index: Int,
        output: MutableList<PreviewModelAssetRequest>,
    ): Promise<List<PreviewModelAssetRequest>> {
        if (index >= seeds.size) return Promise.resolve(output.toList())
        return resolveModelAsset(rootAsset, seeds[index]).flatMap { request ->
            output += request
            resolveModelAssets(rootAsset, seeds, index + 1, output)
        }
    }

    private fun resolveModelAsset(
        rootAsset: ResolvedAsset<ByteArray>,
        seed: ModelAssetSeed,
    ): Promise<PreviewModelAssetRequest> {
        if (seed.binding.type == UiBinding.BindingType.EXPR) {
            return Promise.resolve(seed.toRequest(null))
        }
        val value = seed.binding.literalValue as String
        val target = AssetPathPolicy.relative(seed.baseCanonicalDirectory, value)
        return resolver.resolveRegistered(rootAsset.fileProvenance, target, seed.kind).then { resolved ->
            seed.toRequest(resolved.canonicalPath)
        }
    }

    private fun staticModelRequests(requests: List<PreviewModelAssetRequest>): List<ThreeStaticModelRequest> = requests
        .groupBy(PreviewModelAssetRequest::nodeId)
        .values
        .mapNotNull(::staticModelRequest)
        .distinct()

    private fun staticModelRequest(requests: List<PreviewModelAssetRequest>): ThreeStaticModelRequest? {
        val source = requests.firstOrNull { it.property == PreviewModelAssetProperty.SOURCE } ?: return null
        if (source.requiresRuntimeResolution) return null
        val texture = requests.firstOrNull { it.property == PreviewModelAssetProperty.TEXTURE }
        if (texture?.requiresRuntimeResolution == true) return null
        val sourceValue = source.binding.literalValue as? String ?: return null
        val textureValue = texture?.binding?.literalValue as? String
        return ThreeStaticModelRequest(source.baseCanonicalDirectory, sourceValue, textureValue)
    }

    private fun emptyCandidate(path: String, ir: UiIr, readSet: AuthoritativeReadSet?): PreviewCandidate =
        PreviewCandidate(path, ir, readSet, emptyList(), emptyList())

    private fun compileDiagnostic(failure: CompileResult.Failure): String =
        "源码编译失败：" + failure.diagnostics.joinToString("；") { "${it.code} ${it.message}" }

    private fun chineseFailure(throwable: Throwable): String {
        val message = throwable.message ?: "未知错误"
        return if (message.any { it.code in 0x4E00..0x9FFF }) message else "候选加载失败：$message"
    }

    private data class ModelAssetSeed(
        val nodeId: String,
        val property: String,
        val baseCanonicalDirectory: String,
        val kind: AssetReferenceKind,
        val binding: UiBinding,
    ) {
        fun toRequest(path: String?): PreviewModelAssetRequest = PreviewModelAssetRequest(
            nodeId,
            if (property == SOURCE_PROP) PreviewModelAssetProperty.SOURCE else PreviewModelAssetProperty.TEXTURE,
            baseCanonicalDirectory,
            kind,
            binding,
            path,
        )
    }

    private companion object {
        const val AICR_MIME = "application/x-allincore-ir"
        const val MODEL_PREVIEW_KIND = "modelPreview"
        const val SOURCE_PROP = "source"
        const val TEXTURE_PROP = "texture"
        const val MOCK_SUFFIX = ".mock.json"
        const val STALE_DIAGNOSTIC = "候选加载已被后续请求取代"
    }
}

private fun PreviewCandidate.disposePreparedResources() {
    background?.dispose()
    preparedModels?.dispose()
}

private fun asThrowable(reason: dynamic): Throwable =
    reason as? Throwable ?: RuntimeException(reason?.toString() ?: "未知错误")

private fun <T, R> Promise<T>.flatMap(transform: (T) -> Promise<R>): Promise<R> =
    then(transform).unsafeCast<Promise<R>>()
